// Silo Cube — EDGE engine.
//
// Every edge is something done TO the photo's own edge — never a frame laid on
// top. Two families:
//   • CARVE  — an SVG mask cuts the photo's own edge into a shape (wave, jag,
//              scallop, fade…). The page shows through the cut-away parts.
//   • LIGHT  — the photo's own rim is glowed / darkened / rounded. These draw
//              on an OVERLAY layer that sits ON TOP of the photo (an inset
//              shadow on the container is hidden behind the <img>, so we return
//              an `overlay` style the renderers paint over the image).
// Each edge exposes an "amount" slider (subtle → strong). Carve edges also get
// a second slider: the geometric ones (curvy/scalloped/serrated/zigzag) use it
// as the COUNT (how many waves/scallops/teeth/zigzags); the organic ones
// (jagged/rough/deckled) use it as RANDOMNESS. Everything redraws live.

// ---- catalog (mirrors backend silo_image_gen.EDGE_STYLES order/labels) -----
export const EDGE_CATALOG = [
  { id: "clean", label: "Clean (no edge)" },
  { id: "fade", label: "Fade out" },
  { id: "curvy", label: "Curvy" },
  { id: "rounded", label: "Rounded corners" },
  { id: "scalloped", label: "Scalloped" },
  { id: "serrated", label: "Serrated" },
  { id: "zigzag", label: "Zigzag" },
  { id: "jagged", label: "Jagged" },
  { id: "rough", label: "Grunge / rough" },
  { id: "deckled", label: "Deckled" },
  { id: "vignette", label: "Spotlight vignette" },
  { id: "neon", label: "Neon glow" },
];
export const KNOWN_EDGES = new Set(EDGE_CATALOG.map((e) => e.id));

// edges whose mask carves the photo's shape (page shows through) — the dashboard
// pill renders these transparent (no rounded box clip).
export const CARVE_EDGES = new Set([
  "fade", "curvy", "scalloped", "serrated", "zigzag", "jagged", "rough", "deckled",
]);
export const isCarve = (e) => CARVE_EDGES.has(e);

// slider metadata per edge. `variance` = the second slider's label.
export const EDGE_CONTROLS = {
  fade: { amount: "Fade depth" },
  curvy: { amount: "Wave size", variance: "Number of waves" },
  rounded: { amount: "Roundness" },
  scalloped: { amount: "Scallop size", variance: "Number of scallops" },
  serrated: { amount: "Tooth size", variance: "Number of teeth" },
  zigzag: { amount: "Zigzag size", variance: "Number of zigzags" },
  jagged: { amount: "Jag size", variance: "Randomness" },
  rough: { amount: "Roughness", variance: "Randomness" },
  deckled: { amount: "Feather depth", variance: "Randomness" },
  vignette: { amount: "Darkening" },
  neon: { amount: "Glow / thickness", variance: "Color (rainbow)" },
};
export const hasAmount = (edge) => !!EDGE_CONTROLS[edge];
export const hasVariance = (edge) => !!EDGE_CONTROLS[edge]?.variance;

const AMOUNT_DEFAULTS = {
  fade: 60, curvy: 50, rounded: 35, scalloped: 45, serrated: 45, zigzag: 45,
  jagged: 50, rough: 55, deckled: 35, vignette: 60, neon: 55,
};
const VARIANCE_DEFAULTS = {
  curvy: 35, scalloped: 40, serrated: 40, zigzag: 40, jagged: 50, rough: 50, deckled: 50, neon: 47,
};
export const edgeAmountDefault = (edge) => AMOUNT_DEFAULTS[edge] ?? 50;
export const edgeVarianceDefault = (edge) => VARIANCE_DEFAULTS[edge] ?? 50;

// ---- helpers ---------------------------------------------------------------
const VB_W = 240;
const VB_H = 80;
const r1 = (n) => Math.round(n * 10) / 10;
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

function mulberry32(seed) {
  let t = (seed >>> 0) || 1;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function maskUri(d) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${VB_W} ${VB_H}' preserveAspectRatio='none'><path d='${d}' fill='white'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

// Carve the top & bottom edges with a 0..1 profile function (1 = fully carved in
// by `amp` px). Left/right stay straight, keeping the wide pill shape.
function topBottomMask(fn, amp, samples) {
  const pts = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    pts.push([r1(VB_W * t), r1(amp * clamp01(fn(t)))]);
  }
  for (let i = 0; i <= samples; i++) {
    const t = 1 - i / samples;
    pts.push([r1(VB_W * t), r1(VB_H - amp * clamp01(fn(t)))]);
  }
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) d += ` L${pts[i][0]},${pts[i][1]}`;
  return maskUri(d + " Z");
}

// Random ragged top & bottom (jagged / grunge / deckled).
function randomMask(amp, nodes, seed, spread) {
  const rnd = mulberry32(seed);
  const lo = 1 - spread;
  const topY = [];
  const botY = [];
  for (let i = 0; i <= nodes; i++) topY.push(amp * (lo + rnd() * spread));
  for (let i = 0; i <= nodes; i++) botY.push(amp * (lo + rnd() * spread));
  let d = `M0,${r1(topY[0])}`;
  for (let i = 1; i <= nodes; i++) d += ` L${r1((VB_W * i) / nodes)},${r1(topY[i])}`;
  d += ` L${VB_W},${r1(VB_H - botY[0])}`;
  for (let i = 1; i <= nodes; i++) d += ` L${r1(VB_W * (1 - i / nodes))},${r1(VB_H - botY[i])}`;
  return maskUri(d + " Z");
}

const maskImg = (uri) => ({
  WebkitMaskImage: uri,
  maskImage: uri,
  WebkitMaskSize: "100% 100%",
  maskSize: "100% 100%",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
});

const lerp = (a, b, t) => a + (b - a) * t;
const countOf = (v, lo, hi) => Math.round(lerp(lo, hi, (v ?? 50) / 100));

// ---- the engine: returns inline styles for container + <img> + overlay -----
// overlay (when present) is painted by the renderers as an absolutely-positioned
// div ON TOP of the photo, so rim glows / darkening are actually visible.
export function edgeRenderStyle(edge, amount, variance) {
  const a = typeof amount === "number" ? amount : edgeAmountDefault(edge);
  const v = typeof variance === "number" ? variance : edgeVarianceDefault(edge);
  const af = a / 100;
  const empty = { container: {}, img: {}, overlay: null };

  switch (edge) {
    case "fade": {
      // strong: at 100% only a tiny solid core remains, fading to transparent.
      const solid = r1(80 - af * 72);
      const uri = `radial-gradient(ellipse 100% 100% at center, #000 ${solid}%, rgba(0,0,0,0.15) ${r1(solid + (100 - solid) * 0.75)}%, transparent 100%)`;
      return { container: {}, img: { WebkitMaskImage: uri, maskImage: uri }, overlay: null };
    }
    case "curvy": {
      const amp = 2 + af * 16;
      const waves = countOf(v, 2, 12);
      return { container: {}, img: maskImg(topBottomMask((t) => (1 - Math.cos(2 * Math.PI * waves * t)) / 2, amp, Math.max(96, waves * 18))), overlay: null };
    }
    case "scalloped": {
      const amp = 2 + af * 16;
      const bumps = countOf(v, 3, 14);
      return { container: {}, img: maskImg(topBottomMask((t) => { const u = (t * bumps) % 1; return 1 - Math.sqrt(Math.max(0, 1 - Math.pow(2 * u - 1, 2))); }, amp, Math.max(120, bumps * 18))), overlay: null };
    }
    case "serrated": {
      const amp = 2 + af * 16;
      const teeth = countOf(v, 3, 16);
      return { container: {}, img: maskImg(topBottomMask((t) => (t * teeth) % 1, amp, Math.max(200, teeth * 16))), overlay: null };
    }
    case "zigzag": {
      const amp = 2 + af * 16;
      const teeth = countOf(v, 3, 16);
      return { container: {}, img: maskImg(topBottomMask((t) => { const u = (t * teeth) % 1; return u < 0.5 ? u * 2 : 2 - 2 * u; }, amp, Math.max(200, teeth * 16))), overlay: null };
    }
    case "jagged": {
      const amp = 2 + af * 16;
      const spread = 0.3 + (v / 100) * 0.7;
      return { container: {}, img: maskImg(randomMask(amp, 22, Math.round(v) + 7, spread)), overlay: null };
    }
    case "rough": {
      const amp = 2 + af * 13;
      const spread = 0.4 + (v / 100) * 0.6;
      return { container: {}, img: maskImg(randomMask(amp, 52, Math.round(v) * 3 + 11, spread)), overlay: null };
    }
    case "deckled": {
      const amp = 1.5 + af * 8;
      const spread = 0.25 + (v / 100) * 0.55;
      return { container: {}, img: maskImg(randomMask(amp, 40, Math.round(v) + 3, spread)), overlay: null };
    }
    case "rounded": {
      const rad = r1(af * 46);
      return { container: { borderRadius: `${rad}px` }, img: { borderRadius: `${rad}px` }, overlay: null };
    }
    case "vignette":
      return { container: {}, img: {}, overlay: { boxShadow: `inset 0 0 ${r1(40 + af * 90)}px ${r1(10 + af * 40)}px rgba(0,0,0,${r1(0.35 + af * 0.55)})` } };
    case "neon": {
      // soft glow only (no hard inner ring line); second slider = rainbow hue.
      const hue = Math.round((v / 100) * 360);
      const col = `hsla(${hue}, 100%, 60%, ${r1(0.55 + af * 0.4)})`;
      return { container: {}, img: {}, overlay: { boxShadow: `inset 0 0 ${r1(14 + af * 46)}px ${r1(3 + af * 12)}px ${col}` } };
    }
    default:
      return empty;
  }
}
